/**
 * Rules Catalog Handlers (Reglas tab)
 *
 * Pure command handlers — no side effects, no API calls, no dispatch.
 * One handler per command type. Immutable nested-group spreads.
 */

import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';
import { RulesCatalogState } from './state';
import { RulesCatalogCommand } from './commands';

type ExtractCommand<T extends string> = Extract<RulesCatalogCommand, { type: T }>;

// -------- Data handlers ----------

const handleSetRules = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/data/setRules'>
): RulesCatalogState => ({
  ...state,
  data: {
    ...state.data,
    rules: cmd.payload.rules
  }
});

const handleSetScopeTree = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/data/setScopeTree'>
): RulesCatalogState => ({
  ...state,
  data: {
    ...state.data,
    scopeTree: cmd.payload.scopeTree
  }
});

// -------- UI handlers ----------

const handleSelectScope = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/selectScope'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    selectedScopeId: cmd.payload.scopeId,
    expandedRuleIds: [],
    rulesSearch: ''
  }
});

const handleToggleRuleExpand = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/toggleRuleExpand'>
): RulesCatalogState => {
  const { ruleId } = cmd.payload;
  const isExpanded = state.ui.expandedRuleIds.includes( ruleId );

  return {
    ...state,
    ui: {
      ...state.ui,
      expandedRuleIds: isExpanded
        ? state.ui.expandedRuleIds.filter(( id ) => id !== ruleId )
        : [ ...state.ui.expandedRuleIds, ruleId ]
    }
  };
};

const handleSetRulesFilter = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/setRulesFilter'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    rulesFilter: cmd.payload.filter
  }
});

const handleSetRulesSearch = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/setRulesSearch'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    rulesSearch: cmd.payload.search
  }
});

const handlePinScope = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/pinScope'>
): RulesCatalogState => {
  const { scopeId } = cmd.payload;
  if ( state.data.pinnedScopeIds.includes( scopeId )) {
    return state;
  }

  return {
    ...state,
    data: {
      ...state.data,
      pinnedScopeIds: [ ...state.data.pinnedScopeIds, scopeId ]
    }
  };
};

const handleSetTreeSearch = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/setTreeSearch'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    treeSearch: cmd.payload.search
  }
});

const handleRequestRuleToggle = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/requestRuleToggle'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    pendingToggle: {
      ruleId: cmd.payload.ruleId,
      enabled: cmd.payload.enabled
    }
  }
});

const handleClearPendingToggle = (
  state: RulesCatalogState,
  _cmd: ExtractCommand<'automation/rulesCatalog/ui/clearPendingToggle'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    pendingToggle: null
  }
});

const handleRequestRuleDelete = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/ui/requestRuleDelete'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    pendingDelete: { ruleId: cmd.payload.ruleId }
  }
});

const handleClearPendingDelete = (
  state: RulesCatalogState,
  _cmd: ExtractCommand<'automation/rulesCatalog/ui/clearPendingDelete'>
): RulesCatalogState => ({
  ...state,
  ui: {
    ...state.ui,
    pendingDelete: null
  }
});

// -------- Loading handlers ----------

const handleSetRulesLoading = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/loading/setRulesLoading'>
): RulesCatalogState => ({
  ...state,
  loading: {
    ...state.loading,
    rulesLoading: cmd.payload.isLoading
  }
});

const handleSetRefDataLoading = (
  state: RulesCatalogState,
  cmd: ExtractCommand<'automation/rulesCatalog/loading/setRefDataLoading'>
): RulesCatalogState => ({
  ...state,
  loading: {
    ...state.loading,
    refDataLoading: cmd.payload.isLoading
  }
});

// -------- Handler map ----------

export const handlers: CommandHandlerMap<RulesCatalogState, RulesCatalogCommand> = {
  // Data
  'automation/rulesCatalog/data/setRules': handleSetRules,
  'automation/rulesCatalog/data/setScopeTree': handleSetScopeTree,
  // UI
  'automation/rulesCatalog/ui/selectScope': handleSelectScope,
  'automation/rulesCatalog/ui/toggleRuleExpand': handleToggleRuleExpand,
  'automation/rulesCatalog/ui/setRulesFilter': handleSetRulesFilter,
  'automation/rulesCatalog/ui/setRulesSearch': handleSetRulesSearch,
  'automation/rulesCatalog/ui/pinScope': handlePinScope,
  'automation/rulesCatalog/ui/setTreeSearch': handleSetTreeSearch,
  'automation/rulesCatalog/ui/requestRuleToggle': handleRequestRuleToggle,
  'automation/rulesCatalog/ui/clearPendingToggle': handleClearPendingToggle,
  'automation/rulesCatalog/ui/requestRuleDelete': handleRequestRuleDelete,
  'automation/rulesCatalog/ui/clearPendingDelete': handleClearPendingDelete,
  // Loading
  'automation/rulesCatalog/loading/setRulesLoading': handleSetRulesLoading,
  'automation/rulesCatalog/loading/setRefDataLoading': handleSetRefDataLoading
};
